/**
 * Reine Logik des Verantwortlichkeits-Dropdowns — Filtern, Gruppieren, Toggeln.
 * Bewusst ohne React/DOM, damit sie per Vitest testbar ist (wie linkSlots.ts).
 */

/** Eine wählbare Zeile im Dropdown. */
export interface VerantOption {
    /** Gespeicherter Wert (Katalog-Bezeichnung oder Pseudo-Wert der festen Rollen). */
    value: string;
    label: string;
    /** Bereich des Katalogeintrags; steuert das Icon der Zeile. */
    bereich: string;
    /**
     * Abschnittsüberschrift, wenn sie NICHT der Bereich ist.
     *
     * Seit 08/2026 stehen die Organrollen unter ihrer Kategorie —
     * Unternehmensleitung, Gesellschafter/Beteiligte, Vertretung. Alle übrigen
     * Rollen behalten ihren Bereich als Überschrift: die Zuständigkeit
     * („Steuern", „Fuhrpark", „IT & Daten" …) mit ihren Rollen darunter.
     */
    gruppe?: string;
}

/** Ein Abschnitt der Liste. */
export interface VerantGruppe {
    bereich: string;
    options: VerantOption[];
}

/** Überschrift eines Eintrags: die Kategorie, sonst der Bereich. */
export const gruppeVon = (o: VerantOption): string => o.gruppe || o.bereich;

/**
 * Vergleichsform für die Suche: klein, ohne Diakritika. Damit findet „qualitat"
 * auch „Qualitätsmanagement" — dieselbe Normalisierung nutzt SearchableSelect.
 */
export const norm = (v: string): string =>
    (v || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

/**
 * Filtert nach Suchbegriff. Getroffen wird auf Label UND Bereich — wer
 * „Fuhrpark" tippt, sieht den ganzen Bereich, ohne die Einzelrollen zu kennen.
 * Leerzeichen sind normale Zeichen: „leiter ein" findet „Leiter Einkauf".
 */
export function filterOptions(options: VerantOption[], query: string): VerantOption[] {
    const q = norm(query.trim());
    if (!q) return options;
    // Mehrwortsuche: jedes Teilstück muss irgendwo vorkommen (Reihenfolge egal).
    const teile = q.split(/\s+/).filter(Boolean);
    return options.filter((o) => {
        const heu = `${norm(o.label)} ${norm(o.bereich)} ${norm(o.gruppe ?? "")}`;
        return teile.every((t) => heu.includes(t));
    });
}

/**
 * Gruppiert nach Abschnitt (Kategorie, sonst Bereich). Die Reihenfolge folgt dem
 * ersten Auftreten in `options` — der Aufrufer bestimmt damit die Sortierung,
 * ohne dass hier eine zweite Sortier-Wahrheit entsteht.
 */
export function groupOptions(options: VerantOption[]): VerantGruppe[] {
    const byBereich = new Map<string, VerantOption[]>();
    for (const o of options) {
        const key = gruppeVon(o);
        const liste = byBereich.get(key);
        if (liste) liste.push(o);
        else byBereich.set(key, [o]);
    }
    return Array.from(byBereich, ([bereich, opts]) => ({ bereich, options: opts }));
}

/** Auswahl umschalten — an/ab, Reihenfolge der übrigen bleibt erhalten. */
export function toggleValue(values: string[], value: string): string[] {
    return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/**
 * Flache Liste der sichtbaren Optionen in Anzeige-Reihenfolge — Grundlage für
 * die Tastatur-Navigation (↑/↓ läuft über Gruppengrenzen hinweg).
 */
export function flatten(gruppen: VerantGruppe[]): VerantOption[] {
    return gruppen.flatMap((g) => g.options);
}

/**
 * Bewegt den Tastatur-Fokus zyklisch. `-1` (nichts aktiv) springt bei ↓ auf 0
 * und bei ↑ ans Ende.
 */
export function moveActive(active: number, delta: 1 | -1, laenge: number): number {
    if (laenge <= 0) return -1;
    if (active < 0) return delta === 1 ? 0 : laenge - 1;
    return (active + delta + laenge) % laenge;
}
